import { ChartBarIcon } from '@heroicons/react/24/outline';
import { ClipboardDocumentListIcon, EllipsisVerticalIcon, NoSymbolIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { ActionIcon, Button, Group, Menu, Modal, rem, Stack } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { ProfileCompletionService } from '../../features/ProfileCompletion/services/profileCompletionService';
import { Person, PersonInfo } from '../../models/person.model';
import { UserRole } from '../../models/user.model';
import { PersonService } from '../../services/personService';
import { dateInputStyles, getDateInputProps, nextIconElement, previousIconElement } from '../../styles/dateInputStyles';
import { isPersonCurrentlyUnassigned } from '../../utils/availabilityUtils';
import { getModalZIndex } from '../../utils/zIndexLayers';

interface PersonActionsMenuProps {
  isUnassigned?: boolean;
  isInactive?: boolean;
  onModify?: (e: React.MouseEvent) => void;
  onAssignmentChange?: (e: React.MouseEvent) => void;
  onActivityChange?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  onCalculateCompletion?: (e: React.MouseEvent) => void;
  variant?: 'icon' | 'button';
  fullWidth?: boolean;
  size?: string;
}

export function PersonActionsMenu({
  isUnassigned = false,
  isInactive = false,
  onModify,
  onAssignmentChange,
  onActivityChange,
  onDelete,
  onCalculateCompletion,
  variant = 'button',
  fullWidth = false,
  size = 'xs'
}: PersonActionsMenuProps) {
  const { userRole } = useAuth();
  const isManagerOrAdmin = userRole === UserRole.Manager || userRole === UserRole.Admin;
  const [menuOpened, setMenuOpened] = useState(false);

  const handleMenuItemClick = (e: React.MouseEvent, callback?: (e: React.MouseEvent) => void) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (callback) callback(e);
  };

  return (
    <Menu shadow="md" width={200} position="bottom-end" withinPortal closeOnItemClick={false} opened={menuOpened} onChange={setMenuOpened}>
      <Menu.Target>
        {variant === 'button' ? (
          <Button
            variant="light"
            color="gray.7"
            size={size}
            px="xs"
            leftSection={<EllipsisVerticalIcon width={16} />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            style={{ textAlign: 'center' }}
            fullWidth={fullWidth}
          >
            Actions
          </Button>
        ) : (
          <ActionIcon
            variant="default"
            size={36}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <EllipsisVerticalIcon style={{ width: rem(16) }} />
          </ActionIcon>
        )}
      </Menu.Target>

      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        {/* Only show modify option for managers and admins */}
        {isManagerOrAdmin && onModify && (
          <Menu.Item
            leftSection={<PencilIcon width={14} />}
            onClick={(e) => handleMenuItemClick(e, onModify)}
          >
            Modify
          </Menu.Item>
        )}

        {/* Only show assignment option for managers and admins */}
        {isManagerOrAdmin && onAssignmentChange && (
          <Menu.Item
            leftSection={<ClipboardDocumentListIcon width={14} />}
            onClick={(e) => handleMenuItemClick(e, onAssignmentChange)}
          >
            Assignment
          </Menu.Item>
        )}

        {/* Only show activity option for managers and admins */}
        {isManagerOrAdmin && onActivityChange && (
          <Menu.Item
            leftSection={<NoSymbolIcon width={14} />}
            onClick={(e) => handleMenuItemClick(e, onActivityChange)}
            color={isInactive ? 'green' : 'red'}
          >
            {isInactive ? 'Activate' : 'Deactivate'}
          </Menu.Item>
        )}

        {/* Show divider only if there are other items above */}
        {isManagerOrAdmin && (onModify || onAssignmentChange || onActivityChange) && <Menu.Divider />}

        {/* Calculate profile completion — managers/admins only */}
        {isManagerOrAdmin && onCalculateCompletion && (
          <Menu.Item
            leftSection={<ChartBarIcon width={14} />}
            onClick={(e) => {
              handleMenuItemClick(e, onCalculateCompletion);
              setMenuOpened(false);
            }}
          >
            Recalculate Completeness
          </Menu.Item>
        )}

        {/* Delete option is available to all users */}
        {onDelete && (
          <Menu.Item
            leftSection={<TrashIcon width={14} />}
            onClick={(e) => handleMenuItemClick(e, onDelete)}
            color="red"
          >
            Delete
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

interface AssignmentChangeModalProps {
  person: Person | PersonInfo;
  isOpen: boolean;
  onClose: () => void;
  onSave: (availabilityDate: Date | null) => void;
}

function AssignmentChangeModal({ person, isOpen, onClose, onSave }: AssignmentChangeModalProps) {
  // Check if the date is valid (not SQL default date)
  const isValidDate = (date: string | null | undefined): boolean => {
    if (!date) return false;
    const dateObj = new Date(date);
    return dateObj.getFullYear() > 2000; // Consider dates after 2000 as valid
  };

  // Initialize date state with a valid date or null
  const [availabilityDate, setAvailabilityDate] = useState<Date | null>(
    isValidDate(person.availabilityDate) ? new Date(person.availabilityDate!) : null
  );

  // Update internal state when person changes
  useEffect(() => {
    setAvailabilityDate(isValidDate(person.availabilityDate) ? new Date(person.availabilityDate!) : null);
  }, [person.id, person.availabilityDate]);

  const handleSave = (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onSave(availabilityDate);
  };

  // Prevent event propagation on the modal content
  const handleModalContentClick = (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // Get props from our shared style file
  const dateInputBaseProps = getDateInputProps();

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Assignment"
      size="sm"
      onClick={handleModalContentClick}
      withCloseButton
      closeOnClickOutside={false}
      zIndex={getModalZIndex()}
    >
      <Stack onClick={handleModalContentClick}>
        <DateInput
          label="Availability date"
          placeholder="Date input"
          value={availabilityDate}
          onChange={setAvailabilityDate}
          onClick={(e) => e.stopPropagation()}
          styles={dateInputStyles}
          nextIcon={<span style={{ fontSize: '16px' }}>{nextIconElement()}</span>}
          previousIcon={<span style={{ fontSize: '16px' }}>{previousIconElement()}</span>}
          {...dateInputBaseProps}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={(e) => {
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
            onClose();
          }}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Singleton service instance to prevent multiple instances
const personServiceInstance = new PersonService();

interface ConnectedPersonActionsMenuProps {
  person: Person | PersonInfo;
  isUnassigned?: boolean;
  isInactive?: boolean;
  onModify?: (e: React.MouseEvent) => void;
  onAssignmentChange?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  variant?: 'icon' | 'button';
  fullWidth?: boolean;
  size?: string;
  onRefresh?: () => void;
}

export function ConnectedPersonActionsMenu({
  person,
  isUnassigned,
  isInactive,
  onModify,
  onAssignmentChange: customAssignmentChange,
  onDelete,
  variant,
  fullWidth,
  size,
  onRefresh
}: ConnectedPersonActionsMenuProps) {
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const personIdRef = useRef<number | null>(null);
  // Prevent double calls with this timestamp
  const lastUpdateRef = useRef<number>(0);

  // Clear personIdRef when component unmounts
  useEffect(() => {
    return () => {
      personIdRef.current = null;
    };
  }, []);

  const handleActivityChange = useCallback(async (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // Prevent duplicate calls - if less than 1 second since last update, ignore
    const now = Date.now();
    if (isUpdating || (now - lastUpdateRef.current < 1000)) {
      console.log('Preventing duplicate activity status change');
      return;
    }

    lastUpdateRef.current = now;

    try {
      setIsUpdating(true);
      // Get the updated person with toggled inactive status
      const updatedPerson = await personServiceInstance.changePersonCvActivityStatus(person);

      // Update UI message
      const newStatus = updatedPerson.inactive ? 'inactive' : 'active';

      notifications.show({
        title: 'Success',
        message: `${person.firstName} ${person.lastName} is now ${newStatus}`,
        color: 'green'
      });

      // Update the local person object to match the response
      if (person) {
        person.inactive = updatedPerson.inactive;
      }
    } catch (error) {
      console.error('Error changing activity status:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to change activity status',
        color: 'red'
      });
    } finally {
      setIsUpdating(false);

      // Refresh data as the very last step to avoid cascading updates
      if (onRefresh) {
        setTimeout(() => onRefresh(), 300);
      }
    }
  }, [person, isUpdating, onRefresh]);

  const handleAssignmentChangeClick = useCallback((e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // Never allow double opening of modal
    if (assignmentModalOpen) {
      console.log('Modal already open - ignoring click');
      return;
    }

    // Prevent duplicate calls - if less than 1 second since last update, ignore
    const now = Date.now();
    if (isUpdating || (now - lastUpdateRef.current < 1000)) {
      console.log('Preventing duplicate assignment modal open');
      return;
    }

    lastUpdateRef.current = now;

    // Store the current person ID to avoid mixed state issues
    personIdRef.current = person.id;

    // Always open the modal first - don't call the API directly
    console.log(`Opening assignment modal for person ID: ${person.id}`);
    setAssignmentModalOpen(true);
  }, [person, isUpdating, assignmentModalOpen]);

  const handleSaveAssignment = useCallback(async (availabilityDate: Date | null) => {
    // Safety check - ensure we're operating on the same person
    if (person.id !== personIdRef.current || isUpdating) {
      console.log('Preventing inconsistent person update', {
        personId: person.id,
        personIdRef: personIdRef.current,
        isUpdating
      });
      return;
    }

    // Prevent duplicate calls - if less than 1 second since last update, ignore
    const now = Date.now();
    if (now - lastUpdateRef.current < 500) {
      console.log('Preventing duplicate assignment save');
      return;
    }

    lastUpdateRef.current = now;

    try {
      setIsUpdating(true);

      // Close the modal first to prevent multiple submissions
      setAssignmentModalOpen(false);

      // Create a copy of the person to modify
      const personToUpdate = { ...person };

      // Check if availabilityDate is null or matches the SQL default date
      if (availabilityDate && availabilityDate.getFullYear() > 1) {
        const yyyy = availabilityDate.getFullYear();
        const mm = String(availabilityDate.getMonth() + 1).padStart(2, '0');
        const dd = String(availabilityDate.getDate()).padStart(2, '0');
        personToUpdate.availabilityDate = `${yyyy}-${mm}-${dd}`;
      } else {
        // Set to undefined to avoid sending the default SQL date
        personToUpdate.availabilityDate = undefined;
      }

      console.log(`Updating assignment for person ${person.id}, availabilityDate: ${personToUpdate.availabilityDate}`);

      // Call the service to update
      await personServiceInstance.changeAssignment(person.id, personToUpdate.availabilityDate);

      // Update local person object
      if (person) {
        person.availabilityDate = personToUpdate.availabilityDate;
      }

      // Show success notification
      notifications.show({
        title: 'Success',
        message: `Assignment status updated for ${person.firstName} ${person.lastName}`,
        color: 'green'
      });
    } catch (error) {
      console.error('Error changing assignment status:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to change assignment status',
        color: 'red'
      });
    } finally {
      setIsUpdating(false);
      personIdRef.current = null;

      // Refresh data as the very last step to avoid cascading updates
      if (onRefresh) {
        // Use a timeout to ensure this happens after all state updates
        setTimeout(() => onRefresh(), 300);
      }
    }
  }, [person, isUpdating, onRefresh]);

  // Close modal when person changes
  useEffect(() => {
    setAssignmentModalOpen(false);
    // Also reset updating state when person changes
    setIsUpdating(false);
  }, [person.id]);

  // Cleanup modal state when closing
  const handleCloseModal = useCallback(() => {
    setAssignmentModalOpen(false);
    personIdRef.current = null;
  }, []);

  // This useEffect will log when the component mounts and unmounts
  useEffect(() => {
    console.log(`ConnectedPersonActionsMenu mounted for person ID: ${person.id}`);
    return () => {
      console.log(`ConnectedPersonActionsMenu unmounted for person ID: ${person.id}`);
    };
  }, [person.id]);

  const handleCalculateCompletion = useCallback(async (e: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    try {
      const service = new ProfileCompletionService();
      await service.calculate(person.id as number);
      notifications.show({
        title: 'Profile Recalculated',
        message: `Profile completeness recalculated for ${person.firstName} ${person.lastName}`,
        color: 'green',
        autoClose: 3000
      });
      onRefresh?.();
    } catch (error) {
      console.error('Error calculating profile completion:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to recalculate profile completeness',
        color: 'red',
        autoClose: 5000
      });
    }
  }, [person, onRefresh]);

  return (
    <>
      <PersonActionsMenu
        isUnassigned={isUnassigned ?? isPersonCurrentlyUnassigned(person.availabilityDate)}
        isInactive={isInactive ?? person.inactive}
        onModify={onModify}
        onAssignmentChange={handleAssignmentChangeClick}
        onActivityChange={handleActivityChange}
        onDelete={onDelete}
        onCalculateCompletion={handleCalculateCompletion}
        variant={variant}
        fullWidth={fullWidth}
        size={size}
      />

      {/* Ensure modal is always ready to be shown when needed */}
      <AssignmentChangeModal
        person={person}
        isOpen={assignmentModalOpen && personIdRef.current === person.id}
        onClose={handleCloseModal}
        onSave={handleSaveAssignment}
      />
    </>
  );
} 